/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

const initialCartIncentives = document.querySelector('[data-cart-incentives]');
const initialCartIncentiveSubtotal = Number(initialCartIncentives?.dataset.cartTotalCents);
let cartIncentivePreviousSubtotal = Number.isFinite(initialCartIncentiveSubtotal)
  ? initialCartIncentiveSubtotal
  : null;
let cartIncentiveCelebrationTimer;

/**
 * Returns the persistent cart reward toast. Moving it to the document body
 * prevents cart-drawer section refreshes from removing an active message.
 * @returns {HTMLElement|null}
 */
function getCartIncentiveCelebration() {
  let celebration = document.querySelector('body > [data-incentive-celebration]');
  if (!celebration) {
    celebration = document.querySelector('[data-incentive-celebration]');
    if (celebration) document.body.append(celebration);
  }

  document.querySelectorAll('[data-incentive-celebration]').forEach((duplicate) => {
    if (duplicate !== celebration) duplicate.remove();
  });

  return celebration;
}

/**
 * Hides the cart reward celebration toast.
 */
function hideCartIncentiveCelebration() {
  const celebration = getCartIncentiveCelebration();
  if (!celebration) return;

  window.clearTimeout(cartIncentiveCelebrationTimer);
  cartIncentiveCelebrationTimer = undefined;
  celebration.classList.remove('is-visible');
  celebration.setAttribute('aria-hidden', 'true');
  window.setTimeout(() => {
    if (!celebration.classList.contains('is-visible')) celebration.hidden = true;
  }, 200);
}

/**
 * Shows a non-blocking cart reward celebration toast.
 * @param {string} text - Celebration message.
 */
function showCartIncentiveCelebration(text) {
  const celebration = getCartIncentiveCelebration();
  const celebrationText = celebration?.querySelector('[data-incentive-celebration-text]');
  if (!celebration || !celebrationText) return;

  const incentives = document.querySelector('[data-cart-incentives]');
  if (incentives) {
    const bounds = incentives.getBoundingClientRect();
    celebration.style.top = `${Math.round(bounds.bottom + 10)}px`;
    celebration.style.right = `${Math.max(16, Math.round(window.innerWidth - bounds.right))}px`;
  }

  window.clearTimeout(cartIncentiveCelebrationTimer);
  celebrationText.textContent = text;
  celebration.hidden = false;
  celebration.setAttribute('aria-hidden', 'false');
  window.requestAnimationFrame(() => celebration.classList.add('is-visible'));

  const closeButton = celebration.querySelector('[data-incentive-celebration-close]');
  if (closeButton) closeButton.onclick = hideCartIncentiveCelebration;

  cartIncentiveCelebrationTimer = window.setTimeout(hideCartIncentiveCelebration, 5000);
}

/**
 * Refreshes the tiered cart reward UI from current cart data.
 * @param {object} [cartData] - Optional Shopify Ajax cart response.
 * @returns {Promise<void>}
 */
async function updateCartTieredIncentiveProgress(cartData) {
  try {
    const wrap = document.querySelector('[data-cart-incentives]');
    if (!wrap) return;

    let cart = cartData;
    if (!cart) {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Cart request failed: ${res.status}`);
      cart = await res.json();
    }

    const giftGoal = Number(wrap.dataset.giftThresholdCents) || 15000;
    const shippingEnabled = wrap.dataset.shippingEnabled !== 'false'
      && Number(wrap.dataset.shippingThresholdCents) > 0;
    const shippingGoal = shippingEnabled
      ? Math.max(Number(wrap.dataset.shippingThresholdCents), giftGoal)
      : giftGoal;

    // Full cart total, used only for the displayed subtotal.
    const cartTotal = Number(cart.total_price ?? cart.items_subtotal_price) || 0;

    // Progress is measured on non-gift lines only, so adding the gift can't
    // change the number that unlocked it.
    const giftPropertyKey = '_labor_day_gift';
    const subtotal = Array.isArray(cart.items)
      ? cart.items.reduce((sum, item) => (
        item.properties && item.properties[giftPropertyKey] ? sum : sum + item.final_line_price
      ), 0)
      : cartTotal;

    const giftUnlocked = subtotal >= giftGoal;
    const shippingUnlocked = shippingEnabled && subtotal >= shippingGoal;
    const previousSubtotal = Number.isFinite(cartIncentivePreviousSubtotal)
      ? cartIncentivePreviousSubtotal
      : (Number.isFinite(Number(wrap.dataset.cartTotalCents))
        ? Number(wrap.dataset.cartTotalCents)
        : subtotal);
    const crossedGiftThreshold = previousSubtotal < giftGoal && giftUnlocked;
    const crossedShippingThreshold = shippingEnabled && previousSubtotal < shippingGoal && shippingUnlocked;
    cartIncentivePreviousSubtotal = subtotal;
    wrap.dataset.cartTotalCents = String(subtotal);
    const rawPct = Math.max(0, Math.min(100, (subtotal / shippingGoal) * 100));
    const pct = shippingUnlocked ? 100 : Math.min(99.99, Math.floor(rawPct * 100) / 100);
    const giftName = wrap.dataset.giftName || 'Free gift';
    const giftProductName = wrap.dataset.giftProductName || '';
    const shippingName = wrap.dataset.shippingName || 'Free shipping';
    const currency = cart.currency || wrap.dataset.currency || 'USD';
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
    const formatMoney = (cents) => formatter.format(cents / 100);

    const fill = wrap.querySelector('.cart-incentives__fill');
    const giftMarker = wrap.querySelector('.cart-incentives__marker--gift');
    const shippingMarker = wrap.querySelector('.cart-incentives__marker--shipping');
    const message = wrap.querySelector('[data-incentive-message]');

    if (fill) fill.style.width = `${pct.toFixed(2)}%`;
    wrap.classList.toggle('is-gift-unlocked', giftUnlocked);
    wrap.classList.toggle('is-shipping-unlocked', shippingUnlocked);
    giftMarker?.classList.toggle('is-unlocked', giftUnlocked);
    shippingMarker?.classList.toggle('is-unlocked', shippingUnlocked);

    if (message) {
      const appendText = (text) => message.append(document.createTextNode(text));
      const appendStrong = (text, className) => {
        const strong = document.createElement('strong');
        strong.textContent = text;
        if (className) strong.classList.add(className);
        message.append(strong);
      };

      message.replaceChildren();
      if (!giftUnlocked) {
        appendText('Add ');
        appendStrong(formatMoney(giftGoal - subtotal));
        appendText(' more for your free Labor Day ');
        appendStrong(giftName.toUpperCase(), 'cart-incentives__reward-name');
        appendText('.');
      } else if (shippingEnabled && !shippingUnlocked) {
        appendText('You are ');
        appendStrong(formatMoney(shippingGoal - subtotal));
        appendText(' away from ');
        appendStrong(shippingName.toUpperCase(), 'cart-incentives__reward-name');
        appendText('.');
      } else if (shippingEnabled) {
        appendText("You've unlocked a ");
        appendStrong(giftName.toUpperCase(), 'cart-incentives__reward-name');
        appendText(' and ');
        appendStrong(shippingName.toUpperCase(), 'cart-incentives__reward-name');
        appendText('!');
      } else {
        appendText('Labor Day gift unlocked — your ');
        appendStrong(giftName.toUpperCase(), 'cart-incentives__reward-name');
        appendText(' has been added to your cart.');
      }
    }

    const giftRewardName = giftProductName || giftName;
    if (crossedGiftThreshold && crossedShippingThreshold) {
      showCartIncentiveCelebration(`Congratulations! You've unlocked a ${giftRewardName} and ${shippingName}!`);
    } else if (crossedGiftThreshold) {
      showCartIncentiveCelebration(`Congratulations! You've unlocked a ${giftRewardName}!`);
    } else if (crossedShippingThreshold) {
      showCartIncentiveCelebration(`Congratulations! You've unlocked ${shippingName}!`);
    }

    const itemCount = document.querySelector('[data-cart-item-count]');
    if (itemCount && Number.isFinite(Number(cart.item_count))) {
      itemCount.textContent = `(${cart.item_count})`;
    }

    const subtotalEl = document.querySelector('[data-cart-subtotal]');
        if (subtotalEl) subtotalEl.textContent = formatMoney(cartTotal);
  } catch (e) {
    console.error('Cart incentive progress update error:', e);
  }
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus, updateCartTieredIncentiveProgress */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      // remove globally-attached cart change listeners if we created them (safety)
      if (this._globalCartEvents) {
        this._globalCartEvents.forEach(evt => {
          document.removeEventListener(evt, this._globalCartEventsHandler);
        });
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
          updateCartTieredIncentiveProgress();
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);

      // --- NEW: listen for common global cart events (covers BOGOS/custom add handlers) ---
      // store a list so we can remove them on disconnectedCallback if needed
      this._globalCartEvents = [
        'on:cart:add',
        'on:line-item:change',
        'cart:change',
        'cart:updated',
        'cart:refresh',
        'cart:requestComplete',
        'cart-drawer:open',
        'ajaxify:cart:updated'
      ];
      this._globalCartEventsHandler = (evt) => {
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.filterVariantDisplay === 'function') {
          // slight delay to ensure DOM replacement has completed
          setTimeout(() => cartItems.filterVariantDisplay(), 50);
        }
        if (typeof updateCartTieredIncentiveProgress === 'function') {
          const completedCart = evt.detail?.cart;
          setTimeout(() => updateCartTieredIncentiveProgress(completedCart), completedCart ? 0 : 50);
        }
      };
      this._globalCartEvents.forEach(evt => document.addEventListener(evt, this._globalCartEventsHandler));
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      // Ensure custom add-to-cart flows also evaluate rewards after the new
      // drawer markup has been installed.
      window.requestAnimationFrame(() => updateCartTieredIncentiveProgress());

      // Reapply custom filtering or logic after cart drawer refresh.
      // Use a slightly longer timeout to account for any async DOM replacement from add handlers/apps.
      setTimeout(() => {
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.filterVariantDisplay === 'function') {
          // call twice spaced apart if needed to be extra robust against race conditions
          try {
            cartItems.filterVariantDisplay();
            setTimeout(() => cartItems.filterVariantDisplay(), 100);
          } catch (err) {
            // swallow errors but log for debugging
            // eslint-disable-next-line no-console
            console.warn('filterVariantDisplay failed after renderContents:', err);
          }
        }
      }, 300);
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
